//* Importing useState and useCallback to React
import { useState, useCallback } from "react";

//* Importing the react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Importing the answerSheetAPI
import { answerSheetAPI } from "../answerSheets/answerSheetsAPI";

//? Importing types 
import { type Pagination } from "../../../services/stores/types";


//! Importing the useApplicationDiagrams hook
import { useApplicationDiagrams } from "../applicationDiagrams/useApplicationDiagrams";


//? Importing types from the answerSheetsAPI file
import {
  type CreateAnswerSheetInput,
  type UpdateAnswerSheetInput,
  type AnswerSheetDetails,
} from "./types/types";

/**
 * ? Custom hook `useAnswerSheets` para gerenciar operações relacionadas a answer sheets.
 *
 * @param {string} token - Token de autenticação para requisições à API.
 * @param {string} testId - ID da aplicação para as requisições.
 * @returns {Object} O hook retorna dados, estado de carregamento, erros e funções para interagir com a API.
 */
export const useAnswerSheets = (token: string, testId?: string) => {
  const queryClient = useQueryClient();


  const { refetch: refetchApplicationDiagrams } = useApplicationDiagrams(
    testId || "",
    token
  );
  

  // * States para busca e paginação
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define a query key
  const queryKey = [
    "answerSheets",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Busca answer sheets com react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      answerSheetAPI.getAll(
        testId || "",      
        search,            
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    enabled: !!testId, 
    staleTime: 5 * 60 * 1000,
  });

  // * Extrai os dados e a paginação
  const answerSheets = data?.answerSheets || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers para busca e paginação
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations para criação, atualização e remoção de answer sheets
  const createAnswerSheetMutation = useMutation({
    mutationFn: (answerSheetData: CreateAnswerSheetInput) =>
      answerSheetAPI.create(answerSheetData, token, testId || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["answerSheets"] });
      refetchApplicationDiagrams();
    },
  });

  const updateAnswerSheetMutation = useMutation({
    mutationFn: ({ id, answerSheetData }: UpdateAnswerSheetInput) =>
      answerSheetAPI.update(id, answerSheetData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["answerSheets"] });
    },
  });

  const deleteAnswerSheetMutation = useMutation({
    mutationFn: (id: string) => answerSheetAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["answerSheets"] });
    },
  });

  // TODO: Busca detalhes de um answer sheet pelo ID
  const getAnswerSheetById = useCallback(
    async (id: string): Promise<AnswerSheetDetails | null> => {
      try {
        const answerSheetDetails = await answerSheetAPI.getById(id, token);
        return answerSheetDetails;
      } catch (err) {
        console.error("Error fetching answer sheet by ID:", err);
        return null;
      }
    },
    [token]
  );

  // Função para buscar o status do job de geração do PDF (polling)
  const getStatusJob = useCallback(
    async (diagramGenerationId: string): Promise<any> => {
      try {
        const status = await answerSheetAPI.getStatusJob(
          diagramGenerationId,
          token
        );
        return status;
      } catch (err) {
        console.error("Error fetching job status:", err);
        throw err;
      }
    },
    [token]
  );

  // * Retorna os valores e funções
  return {
    answerSheets,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createAnswerSheet: createAnswerSheetMutation.mutateAsync,
    updateAnswerSheet: updateAnswerSheetMutation.mutateAsync,
    deleteAnswerSheet: deleteAnswerSheetMutation.mutate,
    getAnswerSheetById,
    changePage,
    getStatusJob, 
    isCreating: createAnswerSheetMutation.isPending,
    isUpdating: updateAnswerSheetMutation.isPending,
    isDeleting: deleteAnswerSheetMutation.isPending,
  };
};
